/*
 * The sidebar is a frame around other people's lists: every section in it comes from a module's
 * `sidebarSections` slot (modules.md §8), which is why core can render "Channels" and
 * "Direct messages" without knowing that a chat module exists. Sections are ordered by their own
 * `order`, so switching a module off removes its rows and nothing else.
 *
 * The frame does three things the rows cannot do for themselves (VISUAL.md §7.2). A section's
 * heading is a button that folds it, and the folded set is this account's setting
 * (`core.shell.sidebar_collapsed`), so a column folded on one machine is folded on the next; a
 * folded section still shows the rows its module says must stay — the current conversation, one
 * that names the reader — and the heading says how many it hides. An empty section with nothing to
 * offer is not drawn (*Starred* comes and goes with the stars), one with a `+` keeps its heading,
 * and a column whose every section is empty says so once, with one door, rather than once per
 * section. Both facts are the module's selectors over the model (`SidebarSection.hiddenCount`,
 * `isEmpty`), read here through `useModel` as primitives, so the frame repaints exactly when the
 * answer changes.
 *
 * The column is on every place (`place.ts`). Above 768 px the shell drops it only when no section
 * is registered at all; below, the sidebar is a route of its own (routes/frames.tsx) and this is
 * what that route renders.
 */

import { Suspense } from "react"
import { ListState } from "../../ui/index.ts"
import { t, translate } from "../i18n/index.ts"
import type { SidebarSection } from "../modules/registry.ts"
import { useActiveConversation, useModel } from "./context.tsx"
import { Icon } from "./icons.tsx"
import { useSidebarSections } from "./place.ts"
import { useSidebarCollapsed } from "./preferences.tsx"
import "./shell.css"

export function Sidebar() {
	const sections = useSidebarSections()
	const [folded, setFolded] = useSidebarCollapsed()
	// One character per section — "1" for empty — so the selector answers a primitive and the
	// column repaints only when some section's emptiness changes. A section without `isEmpty` is
	// never empty, so a column with one such section never is.
	const emptiness = useModel((model) =>
		sections.map((section) => (section.isEmpty?.(model) === true ? "1" : "0")).join(""),
	)
	return (
		<nav className="tb-sidebar" aria-label={t("shell.region.sidebar")}>
			<SidebarColumn
				sections={sections}
				empty={sections.map((_, at) => emptiness[at] === "1")}
				folded={folded}
				onToggle={setFolded}
			/>
		</nav>
	)
}

function SidebarColumn({
	sections,
	empty,
	folded,
	onToggle,
}: {
	sections: readonly SidebarSection[]
	/** Per section, in order: whether its `isEmpty` answered true. */
	empty: readonly boolean[]
	folded: ReadonlySet<string>
	onToggle: (sectionId: string, collapsed: boolean) => void
}) {
	if (sections.length === 0 || empty.every(Boolean)) {
		// One sentence and one door for the whole column: a heading per section over "nothing
		// here" each was the same news said three times (D-06 S20). The door is the first
		// section's that offers one — chat's *Create a channel*.
		const Door = sections.find((section) => section.Door !== undefined)?.Door
		return (
			<ListState.Empty
				title={t("shell.sidebar.empty")}
				action={Door === undefined ? undefined : <Door />}
			/>
		)
	}
	// An empty section that offers nothing to do — *Starred* with no star — is not a section at
	// all and is not drawn; one with a `+` keeps its heading, because the heading is where the
	// way to a first row is.
	return sections.map((section, at) =>
		empty[at] === true && section.Actions === undefined ? null : (
			<SidebarSectionFrame
				key={section.id}
				section={section}
				collapsed={folded.has(section.id)}
				onToggle={(collapsed) => onToggle(section.id, collapsed)}
			/>
		),
	)
}

function SidebarSectionFrame({
	section,
	collapsed,
	onToggle,
}: {
	section: SidebarSection
	collapsed: boolean
	onToggle: (collapsed: boolean) => void
}) {
	const active = useActiveConversation()
	// The count is the module's to know and this frame's to say; it is asked only while the section
	// is folded, because an unfolded section hides nothing whatever the module would answer.
	const hidden = useModel((model) => (collapsed ? (section.hiddenCount?.(model, active) ?? 0) : 0))
	return (
		<section className="tb-sidebar__section">
			{/* Title and controls on one line: what a section offers belongs beside its name, not
			    under it where it reads as the list's first row (UX §1.2, X-A3 V15). The heading
			    *is* the fold button, so there is one control for one fact, and the arrow in front of
			    the caption says which way it is folded. */}
			<div className="tb-sidebar__header">
				<h2 className="tb-sidebar__title">
					<button
						type="button"
						className="tb-sidebar__toggle"
						aria-expanded={!collapsed}
						onClick={() => onToggle(!collapsed)}
					>
						{/* In the slot the rows draw their glyph in, so the caption begins on the line
						    every row label begins on (X-B2 B2-05). */}
						<span className="tb-sidebar__chevron">
							<Icon name="chevronDown" size={16} />
						</span>
						{translate(section.titleKey)}
						{hidden > 0 ? (
							<span className="tb-sidebar__hidden">
								{t("shell.sidebar.hidden", { count: hidden })}
							</span>
						) : null}
					</button>
				</h2>
				{section.Actions === undefined ? null : <section.Actions />}
			</div>
			{/* A section's rows may be a `lazy()` of a chunk this reader has not fetched yet: a module
			    whose screens are outside the entry chunk cannot hand over a static component here
			    without pulling its area back in (registry.ts `SlotComponent`). The fallback is the
			    skeleton every list in the product waits behind, which draws nothing at all for the
			    first `--delay-reveal` — so a cached chunk shows rows and never a flash. */}
			<Suspense fallback={<ListState.Loading rows={3} />}>
				<section.Rows collapsed={collapsed} />
			</Suspense>
		</section>
	)
}
